import { useState, useCallback, useRef } from 'react'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { isURL } from 'validator'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

const WEBSITE_BLOCKED_ERROR_MSG = 'This website blocks automated access. Please try another site.'
const POLLING_INTERVAL = 500
const MAX_POLLING_ATTEMPTS = 120 // 60 seconds at 500ms intervals

export type ScreenshotStatus = 'idle' | 'pending' | 'processing' | 'completed' | 'failed'

interface AsyncScreenshotResponse {
  request_id: string
  status: ScreenshotStatus
}

interface ScreenshotStatusResponse {
  status: ScreenshotStatus
  screenshot_url?: string
  error?: string
}

interface UseAsyncScreenshotOptions {
  onSuccess?: (screenshotUrl: string) => void
  onError?: (error: string) => void
}

interface UseAsyncScreenshotReturn {
  screenshotUrl: string | null
  status: ScreenshotStatus
  error: string | null
  isLoading: boolean
  createScreenshot: (url: string, signal?: AbortSignal) => Promise<string>
  reset: () => void
}

export class WebsiteBlockedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'WebsiteBlockedError'
  }
}

export const useAsyncScreenshot = (options?: UseAsyncScreenshotOptions): UseAsyncScreenshotReturn => {
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<ScreenshotStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef<string | null>(null)
  const pollingAttemptsRef = useRef(0)

  const reset = useCallback(() => {
    setScreenshotUrl(null)
    setStatus('idle')
    setError(null)
    requestIdRef.current = null
    pollingAttemptsRef.current = 0
  }, [])

  const createScreenshot = useCallback(
    async (url: string, signal?: AbortSignal): Promise<string> => {
      reset()

      // Normalize URL
      if (
        !isURL(url, { require_tld: true, require_protocol: true }) &&
        isURL(`https://${url}`, { require_tld: true, require_protocol: true })
      ) {
        url = `https://${url}`
      }

      setStatus('pending')

      const response = await sendToApi<AsyncScreenshotResponse>(`/screenshot-url`, {
        method: 'POST',
        signal,
        body: {
          url,
          async: true,
        },
      })

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        setStatus('failed')
        setError('Failed to start screenshot')
        options?.onError?.('Failed to start screenshot')
        throw new Error('Failed to start screenshot')
      }

      const { request_id } = response.data
      requestIdRef.current = request_id
      pollingAttemptsRef.current = 0

      // Poll for result
      return new Promise<string>((resolve, reject) => {
        const pollStatus = async () => {
          if (signal?.aborted) {
            reject(new DOMException('The operation was aborted.', 'AbortError'))
            return
          }

          if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
            setStatus('failed')
            setError('Screenshot request timed out')
            options?.onError?.('Screenshot request timed out')
            reject(new Error('Screenshot request timed out'))
            return
          }

          pollingAttemptsRef.current += 1

          const statusResponse = await sendToApi<ScreenshotStatusResponse>(`/screenshot-url/${request_id}`, {
            method: 'GET',
            signal,
          })

          if (statusResponse.type !== SUCCESS_RESPONSE_TYPE) {
            setStatus('failed')
            setError('Failed to check screenshot status')
            options?.onError?.('Failed to check screenshot status')
            reject(new Error('Failed to check screenshot status'))
            return
          }

          const data = statusResponse.data

          if (data.status === 'completed' && data.screenshot_url) {
            setStatus('completed')
            setScreenshotUrl(data.screenshot_url)
            options?.onSuccess?.(data.screenshot_url)
            resolve(data.screenshot_url)
          } else if (data.status === 'failed') {
            setStatus('failed')
            const errorMsg = data.error || 'Screenshot failed'
            setError(errorMsg)
            if (errorMsg.includes('timed out')) {
              showErrorMessageToast(WEBSITE_BLOCKED_ERROR_MSG)
              reject(new WebsiteBlockedError(WEBSITE_BLOCKED_ERROR_MSG))
            } else {
              options?.onError?.(errorMsg)
              reject(new Error(errorMsg))
            }
          } else {
            setStatus(data.status)
            // Continue polling
            setTimeout(pollStatus, POLLING_INTERVAL)
          }
        }

        pollStatus()
      })
    },
    [reset, options],
  )

  return {
    screenshotUrl,
    status,
    error,
    isLoading: status === 'pending' || status === 'processing',
    createScreenshot,
    reset,
  }
}
